<template>
    <v-dialog v-model="dialog" persistent max-width="500px">
        <template v-slot:activator="{ on }">
            <v-btn v-if="smallIcon" icon text v-on="on">
                <v-icon>fa-edit</v-icon>
            </v-btn>

            <v-btn v-else color="info" dark v-on="on" :disabled="!isEditable" class="mb-2">
                <v-icon class="pr-1">fa-plus-circle</v-icon>
                Add Reward
            </v-btn>
        </template>
        <v-card>
            <v-card-title>
                <span class="text-h5" v-if="value">Edit Reward</span>
                <span class="text-h5" v-else>Add Reward</span>
            </v-card-title>
            <v-card-text>
                <v-container>
                    <v-form v-model="isFormValid" ref="form" :disabled="!isEditable">
                      <v-row>
                          <v-col cols="12">
                            <v-select label="Reward Type" outlined hide-details dense :items="selectableRewardTypes" v-model="selectedRewardType" :rules="requiredRule" :disabled="rewardTypeDisabled || !isEditable"/>
                          </v-col>
                      </v-row>

                      <atm-reward-editor v-if="selectedRewardType === AtmReward.CLASS" v-model="workingReward" :atmSetting="atmSetting"/>
                      <cash-back-reward-editor v-if="selectedRewardType === CashBackReward.CLASS" v-model="workingReward"/>
                      <eats-reward-editor v-if="selectedRewardType === EatsReward.CLASS" v-model="workingReward"/>
                      <streaming-reward-editor v-if="selectedRewardType === StreamingReward.CLASS" v-model="workingReward"/>
                      <fee-reduction-reward-editor v-if="selectedRewardType === FeeReductionReward.CLASS" v-model="workingReward"/>
                      <flex-reward-editor v-if="selectedRewardType === FlexReward.CLASS" v-model="workingReward"/>
                      <interest-reward-editor v-if="selectedRewardType === InterestReward.CLASS" :productHasInterestReward="productHasInterestReward"
                        :level="level" :editingExistingReward="editingExistingReward" v-model="workingReward"/>
                      <tunes-grant-reward-editor v-if="selectedRewardType === TunesGrantReward.CLASS" v-model="workingReward"/>
                      <ach-aggregate-fee-reduction-reward-editor v-if="selectedRewardType === AchAggregateFeeReductionReward.CLASS" v-model="workingReward"/>
                      <estatement-fee-reduction-reward-editor v-if="selectedRewardType === EstatementFeeReductionReward.CLASS" v-model="workingReward"/>
                      <ib-login-fee-reduction-reward-editor v-if="selectedRewardType === IbLoginFeeReductionReward.CLASS" v-model="workingReward"/>
                      <minimum-balance-fee-reduction-reward-editor v-if="selectedRewardType === MinimumBalanceFeeReductionReward.CLASS" v-model="workingReward"/>
                      <per-ach-trx-type-fee-reduction-reward-editor v-if="selectedRewardType === PerAchTrxTypeFeeReductionReward.CLASS" v-model="workingReward"/>
                      <per-pos-trx-type-fee-reduction-reward-editor v-if="selectedRewardType === PerPosTrxTypeFeeReductionReward.CLASS" v-model="workingReward"/>
                      <waive-service-fee-reward-editor v-if="selectedRewardType === WaiveServiceFeeReward.CLASS" v-model="workingReward"/>

                    </v-form>
                </v-container>
            </v-card-text>

            <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn large @click="cancel">Cancel</v-btn>
                <v-btn id="saveButton" large class="blue white--text" @click="save" :disabled="!isFormValid || !workingReward || !isEditable" ref="okButton">
                        Ok
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script lang="ts">
import Vue from 'vue';
import {
  Component, Prop, Watch,
} from 'vue-property-decorator';
import {
  IReward, AchAggregateFeeReductionReward, AtmReward, CashBackReward,
  EatsReward, EstatementFeeReductionReward, FeeReductionReward, FlexReward, IbLoginFeeReductionReward,
  InterestReward, MinimumBalanceFeeReductionReward, PerAchTrxTypeFeeReductionReward, PerPosTrxTypeFeeReductionReward,
  StreamingReward, TunesGrantReward, WaiveServiceFeeReward,
} from '@/api/product/levelrewards/rewards';
import { ATM } from '@/api/atmsettings';
import AtmRewardEditor from './AtmRewardEditor.vue';
import CashBackRewardEditor from './CashBackRewardEditor.vue';
import EatsRewardEditor from './EatsRewardEditor.vue';
import StreamingRewardEditor from './StreamingRewardEditor.vue';
import FeeReductionRewardEditor from './FeeReductionRewardEditor.vue';
import FlexRewardEditor from './FlexRewardEditor.vue';
import InterestRewardEditor from './InterestRewardEditor.vue';
import TunesGrantRewardEditor from './TunesGrantRewardEditor.vue';
import AchAggregateFeeReductionRewardEditor from './AchAggregateFeeReductionRewardEditor.vue';
import EstatementFeeReductionRewardEditor from './EstatementFeeReductionRewardEditor.vue';
import IbLoginFeeReductionRewardEditor from './IbLoginFeeReductionRewardEditor.vue';
import MinimumBalanceFeeReductionRewardEditor from './MinimumBalanceFeeReductionRewardEditor.vue';
import PerAchTrxTypeFeeReductionRewardEditor from './PerAchTrxTypeFeeReductionRewardEditor.vue';
import PerPosTrxTypeFeeReductionRewardEditor from './PerPosTrxTypeFeeReductionRewardEditor.vue';
import WaiveServiceFeeRewardEditor from './WaiveServiceFeeRewardEditor.vue';

@Component({
  components: {
    AtmRewardEditor,
    CashBackRewardEditor,
    EatsRewardEditor,
    StreamingRewardEditor,
    FeeReductionRewardEditor,
    FlexRewardEditor,
    InterestRewardEditor,
    TunesGrantRewardEditor,
    AchAggregateFeeReductionRewardEditor,
    EstatementFeeReductionRewardEditor,
    IbLoginFeeReductionRewardEditor,
    MinimumBalanceFeeReductionRewardEditor,
    PerAchTrxTypeFeeReductionRewardEditor,
    PerPosTrxTypeFeeReductionRewardEditor,
    WaiveServiceFeeRewardEditor,
  },
})
export default class EditRewardDialog extends Vue {
  @Prop()
  value!:IReward;

  @Prop({ default: false })
  smallIcon!:boolean;

  @Prop()
  productType!:string;

  @Prop({ default: false })
  rewardTypeDisabled!:boolean;

  @Prop({ default: false })
  productHasInterestReward!:boolean;

  @Prop()
  level!: number;

  @Prop({ default: false })
  isEditable!: boolean;

  @Prop()
  atmSetting!: ATM | null;

  workingReward:IReward | null = null;

  dialog:boolean = false;

  isFormValid:boolean = true;

  editingExistingReward = false;

  selectedRewardType: string | null = null;

  selectableRewardTypes: any[] = [];

  // Make visible to the template
  AtmReward: any = AtmReward;

  CashBackReward: any = CashBackReward;

  EatsReward: any = EatsReward;

  StreamingReward: any = StreamingReward;

  FeeReductionReward: any = FeeReductionReward;

  FlexReward: any = FlexReward;

  InterestReward: any = InterestReward;

  TunesGrantReward: any = TunesGrantReward;

  AchAggregateFeeReductionReward: any = AchAggregateFeeReductionReward;

  EstatementFeeReductionReward: any = EstatementFeeReductionReward;

  IbLoginFeeReductionReward: any = IbLoginFeeReductionReward;

  MinimumBalanceFeeReductionReward:any = MinimumBalanceFeeReductionReward;

  PerAchTrxTypeFeeReductionReward:any = PerAchTrxTypeFeeReductionReward;

  PerPosTrxTypeFeeReductionReward:any = PerPosTrxTypeFeeReductionReward;

  WaiveServiceFeeReward:any = WaiveServiceFeeReward;

  requiredRule:Array<any> = [(value: any) => {
    if (value === null || value === undefined || (Array.isArray(value) && value.length === 0)) {
      return 'Required';
    }
    return true;
  }];

  mounted() {
    this.initialize();
  }

  /* eslint-disable no-underscore-dangle */
  @Watch('dialog')
  initialize() {
    if (this.dialog) {
      if (this.value) {
        this.workingReward = this.value.clone();
        this.selectedRewardType = this.value._class;
        this.editingExistingReward = true;
      } else {
        this.workingReward = null;
        this.selectedRewardType = null;
        this.editingExistingReward = false;
      }
      this.buildSelectableRewardTypes();
    }
  }

  buildSelectableRewardTypes() {
    const rewardTypes:any[] = [];

    if (this.productType === 'INCONTROL_BURN_DOWN') {
      rewardTypes.push({
        text: AchAggregateFeeReductionReward.NAME,
        value: AchAggregateFeeReductionReward.CLASS,
      },
      {
        text: EstatementFeeReductionReward.NAME,
        value: EstatementFeeReductionReward.CLASS,
      },
      {
        text: IbLoginFeeReductionReward.NAME,
        value: IbLoginFeeReductionReward.CLASS,
      },
      {
        text: MinimumBalanceFeeReductionReward.NAME,
        value: MinimumBalanceFeeReductionReward.CLASS,
      },
      {
        text: PerAchTrxTypeFeeReductionReward.NAME,
        value: PerAchTrxTypeFeeReductionReward.CLASS,
      },
      {
        text: PerPosTrxTypeFeeReductionReward.NAME,
        value: PerPosTrxTypeFeeReductionReward.CLASS,
      },
      {
        text: WaiveServiceFeeReward.NAME,
        value: WaiveServiceFeeReward.CLASS,
      });
    } else {
      rewardTypes.push({
        text: AtmReward.NAME,
        value: AtmReward.CLASS,
      },
      {
        text: CashBackReward.NAME,
        value: CashBackReward.CLASS,
      },
      {
        text: EatsReward.NAME,
        value: EatsReward.CLASS,
      },
      {
        text: FeeReductionReward.NAME,
        value: FeeReductionReward.CLASS,
      },
      {
        text: FlexReward.NAME,
        value: FlexReward.CLASS,
      },
      {
        text: InterestReward.NAME,
        value: InterestReward.CLASS,
      },
      {
        text: StreamingReward.NAME,
        value: StreamingReward.CLASS,
      },
      {
        text: TunesGrantReward.NAME,
        value: TunesGrantReward.CLASS,
      });
    }

    this.selectableRewardTypes = rewardTypes.sort((a:any, b:any) : number => a.text.localeCompare(b.text));
  }

  @Watch('selectedRewardType')
  onNewSelectedRewardType() {
    if (this.selectedRewardType && (this.workingReward === null || this.workingReward._class !== this.selectedRewardType)) {
      switch (this.selectedRewardType) {
        case AtmReward.CLASS: {
          this.workingReward = new AtmReward();
          break;
        }

        case CashBackReward.CLASS: {
          this.workingReward = new CashBackReward();
          break;
        }

        case EatsReward.CLASS: {
          this.workingReward = new EatsReward();
          break;
        }

        case FeeReductionReward.CLASS: {
          this.workingReward = new FeeReductionReward();
          break;
        }

        case FlexReward.CLASS: {
          this.workingReward = new FlexReward();
          break;
        }

        case InterestReward.CLASS: {
          this.workingReward = new InterestReward();
          break;
        }

        case StreamingReward.CLASS: {
          this.workingReward = new StreamingReward();
          break;
        }

        case TunesGrantReward.CLASS: {
          this.workingReward = new TunesGrantReward();
          break;
        }

        case AchAggregateFeeReductionReward.CLASS: {
          this.workingReward = new AchAggregateFeeReductionReward();
          break;
        }

        case EstatementFeeReductionReward.CLASS: {
          this.workingReward = new EstatementFeeReductionReward();
          break;
        }

        case IbLoginFeeReductionReward.CLASS: {
          this.workingReward = new IbLoginFeeReductionReward();
          break;
        }

        case MinimumBalanceFeeReductionReward.CLASS: {
          this.workingReward = new MinimumBalanceFeeReductionReward();
          break;
        }

        case PerAchTrxTypeFeeReductionReward.CLASS: {
          this.workingReward = new PerAchTrxTypeFeeReductionReward();
          break;
        }

        case PerPosTrxTypeFeeReductionReward.CLASS: {
          this.workingReward = new PerPosTrxTypeFeeReductionReward();
          break;
        }

        case WaiveServiceFeeReward.CLASS: {
          this.workingReward = new WaiveServiceFeeReward();
          break;
        }

        default:
          throw new Error(`Unknown reward type: ${this.selectedRewardType}`);
      }
    }
  }

  cancel() {
    const form: any = this.$refs.form;
    form.reset();
    this.dialog = false;
  }

  save() {
    this.$emit('input', this.workingReward);
    this.dialog = false;
  }

  /* eslint-enable no-underscore-dangle */
}
</script>

<style lang="scss" scoped>
</style>
